<template>
    <div
        class="recommended-action-card"
        role="group"
        aria-label="Recommended Action"
    >
        <p
            class="action-message"
            :aria-label="unescapeHTML(action.message)"
            v-html="action.message"
        />
        <button
            class="action-close-button"
            data-testid="fm-dash-action-close-button"
            aria-label="Dismiss Action"
            @click="dismissAction"
        >
            <FontAwesomeIcon :icon="faClose" size="xl" />
        </button>
        <Button
            class="action-trigger-button"
            data-testid="fm-dash-action-trigger-button"
            :label="action.label ?? action.type"
            outlined
            severity="secondary"
            @click="triggerAction"
        />
    </div>
</template>

<script lang="ts" setup>
import Button from 'primevue/button'
import { RecommendedAction } from '@/entities/frontlineManagerDash'
import { faClose } from 'fontawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from 'fontawesome/vue-fontawesome'
import { unescapeHTML } from '@/utils/string'

interface Props {
    action: RecommendedAction
}
const props = defineProps<Props>()
const emit = defineEmits(['dismissAction', 'triggerAction'])

const dismissAction = () => {
    emit('dismissAction')
}

const triggerAction = () => {
    emit('triggerAction')
}
</script>

<style lang="less" scoped>
@import '~@/styles/prime-vue/variables.less';
@import '~@/styles/rain/variables.less';

.recommended-action-card {
    position: relative;
    min-height: 64px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-radius: @radius-xl;
    border: 1px solid var(--p-gray-200);
    padding: @spacing-2xl;
    gap: @spacing-md;
}

.action-message {
    margin: 0;
    font-size: @fontSize-md;
    line-height: @display-xs;

    :deep(&--promoter) {
        font-weight: @fontWeight-bold;
        color: var(--p-success-600);
    }
    :deep(&--detractor) {
        font-weight: @fontWeight-bold;
        color: var(--p-error-600);
    }
}

.action-trigger-button {
    height: fit-content;
    padding: 10px 14px;
    flex-shrink: 0;
    font-size: @fontSize-base;
    color: var(--p-gray-700);
    box-shadow: @shadow-xs;
    text-transform: capitalize;
}

.action-close-button {
    position: absolute;
    right: 1px;
    top: 1px;
    border: unset;
    background: unset;
    padding: @spacing-md;
    color: var(--p-gray-400);
    cursor: pointer;
    transition: color 0.2s ease-in;
}
.action-close-button:hover,
.action-close-button:focus {
    color: var(--p-error-600);
}

@media (min-width: @tabletBreakpoint) {
    .recommended-action-card {
        padding: @spacing-2xl @spacing-4xl;
    }
}

@media (min-width: @desktopBreakpoint) {
    .recommended-action-card {
        flex-direction: row;
        text-align: left;
        padding: @spacing-4xl @spacing-2xl;
    }

    .action-message {
        line-height: @display-sm;
    }
}
</style>
